import { useEffect } from "react";
import { useSelector } from "react-redux";
import { generatePath, useLocation, useNavigate, useParams } from "react-router-dom";
import { selectUserData } from "redux/AuthSlice/index.slice";
import { getCompletePathList } from "route";
import { AuthDriver as authDriver, modalNotification, removeLocalStorageToken } from "utils";

/**
 * Functional component representing the main application layout.
 * Manages user authentication, redirects, and renders children components.
 * @param {Object} setRedirectPath - Function to set the redirection path.
 * @param {ReactNode} children - The components to be rendered within the layout.
 */
function AppLayout({ setRedirectPath, children }) {
  const location = useLocation();
  const params = useParams();
  const userData = useSelector(selectUserData);
  const navigate = useNavigate();
  
  /**
   * Function to generate a path based on the provided data and parameters.
   * @param {string} data - The path template.
   * @returns {string} - The generated path.
   */
  function getGeneratedPath(data) {
    try {
      return generatePath(data, params);
    } catch (error) {
      return data;
    }
  }

  /**
   * Normalize path by removing trailing slash for consistent comparison
   * @param {string} path - The path to normalize
   * @returns {string} - The normalized path
   */
  function normalizePath(path) {
    return path.endsWith('/') && path.length > 1 ? path.slice(0, -1) : path;
  }

  // Get all available routes
  const allRoutes = getCompletePathList();
  
  // Find the active route based on the current browser path
  const activeRoute = allRoutes.find((e) => {
    const routePath = normalizePath(getGeneratedPath(e.key));
    const browserPath = normalizePath(location.pathname);
    return routePath === browserPath;
  });

  // If no exact match, try to find a partial match (for dynamic routes)
  const fallbackRoute = !activeRoute ? allRoutes.find((e) => {
    const routePath = normalizePath(getGeneratedPath(e.key));
    const browserPath = normalizePath(location.pathname);
    // Check if the browser path starts with the route path (for nested routes)
    return browserPath.startsWith(routePath) && e.path !== "*";
  }) : null;

  const finalRoute = activeRoute || fallbackRoute;
  const isPrivate = finalRoute?.private;
  const isValid = authDriver(finalRoute, userData, location.pathname);

  /**
   * Function to check the validity of the user and redirect accordingly.
   */
  function checkValid() {
    if (!isValid) {
      let publicPath = "/instructor";
      let privatePath = "/instructor/dashboard";
      let checkData = userData?.user?.role;
      
      // Check if this is an admin route
      if (location.pathname.includes("backend-hf8a2w-91zj1b")) {
        publicPath = "/backend-hf8a2w-91zj1b";
        privatePath = "/backend-hf8a2w-91zj1b/dashboard";
      } else if (checkData === "instructor") {
        privatePath = "/instructor/dashboard";
        publicPath = "/instructor";
      }

      if (isPrivate === true) {
        /** ******* If route type is private but not*****************
         * ******* able to prove their identity as valid entity*****
         * ********* so redirect it to public route******** */
        modalNotification({
          type: "warning",
          message: "Your Token is expired. Please login again.",
        });
        removeLocalStorageToken();
        navigate(publicPath);
        // setRedirectPath(publicPath);
        // return false;
      } else if (isPrivate === false) {
        /** ******* If route type is public but not*****************
         * ******* able to prove their identity as valid entity*****
         * ********* so redirect it to private route******** */
        setRedirectPath(privatePath);
      }
    } else {
      // setRedirectPath(location.pathname);
    }
  }

  useEffect(() => {
    checkValid();
  }, [location.pathname]);

  // Add debug logging in development
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.log('AppLayout Debug:', {
      pathname: location.pathname,
      normalizedPath: normalizePath(location.pathname),
      activeRoute,
      fallbackRoute,
      finalRoute,
      isPrivate,
      isValid,
      userData,
      totalRoutes: allRoutes.length,
      availableRoutePaths: allRoutes.map(r => r.key).slice(0, 10) // Show first 10 routes
    });
  }

  // Render children components only if the user is valid
  return <>{isValid && children}</>;
}

export default AppLayout;
